import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { API_URL } from '@constants';
import {
  Auth,
  AuthenticationMethod,
  HttpStatusCode,
  SystemSettings,
  UserSettings,
} from '@models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  RoutesService,
  SettingsService,
  ToastService,
  UserSessionService,
} from '@services';
import { logError } from '@shared/utils';
import {
  AppState,
  onSessionEnd,
  resetUser,
  setIsLoggedIn,
  setLastSearch,
  setSessionToken,
  setSystemSettings,
} from '@store';
import { isEmpty } from 'lodash-es';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  firstValueFrom,
  map,
  of,
  retry,
  throwError,
  timeout,
} from 'rxjs';
import { ModalService } from './modal.service';
import { UserBeanService } from './user-bean.service';

const headers = {
  'Content-Type': 'application/x-www-form-urlencoded',
};

interface PasswordPolicy {
  type: string;
  enabled: boolean;
}

interface PasswordPolicyConfig {
  minLength: number;
  minQuality: number;
  policies: PasswordPolicy[];
}

interface LoginResponse {
  access_token?: string;
  error_description?: string;
  authMethod?: number | 'otprenew';
  mustAcceptConditions?: boolean;
  mustChangePwd?: boolean;
  settings?: UserSettings;
  removedSession?: { sessionStarted: number };
  url?: string;
  conditions?: { text: string; fileSize: number };
  policyPwd?: PasswordPolicyConfig;
  redirectUrl?: string;
  subtype?: number;
  signatureFastTrack?: boolean;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

@Service()
export class AuthenticationService {
  http = inject(HttpClient);
  router = inject(Router);
  location = inject(Location);
  toastService = inject(ToastService);
  userSessionService = inject(UserSessionService);
  settingsService = inject(SettingsService);
  userBeanService = inject(UserBeanService);
  routesService = inject(RoutesService);
  modalService = inject(ModalService);
  translate = inject(TranslateService);
  cookieService = inject(CookieService);
  store = inject<Store<AppState>>(Store);

  userBean?: UserSettings;
  isLoggedInState!: boolean;
  systemSettings!: SystemSettings;
  sessionToken!: Nullable<string>;

  constructor() {
    this.store
      .select((state) => state)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.userBean = state.user;
        this.isLoggedInState = state.auth.isLoggedIn;
        this.systemSettings = state.settings.system;
        this.sessionToken = state.auth.sessionToken;
      });

    onSessionEnd(this.clearAdminSession);
  }

  notifyError = (error: { error_description?: string }, step?: number) => {
    if (step !== 1 && error?.error_description) {
      this.toastService.show({
        text: error.error_description,
        type: 'danger',
      });
    }
  };

  setToken = (response: { access_token: string }) => {
    const sessionToken = response.access_token;

    this.store.dispatch(setSessionToken({ payload: sessionToken }));
    this.clearAdminSession();
  };

  clearAdminSession = () => {
    this.cookieService.delete('adminSessionToken');
  };

  resetToken = () => {
    this.store.dispatch(setSessionToken({ payload: null }));
  };

  authenticateSession = (response: LoginResponse) => {
    if (response?.access_token) {
      this.setToken(response as { access_token: string });

      return;
    }

    if (response?.error_description) {
      this.toastService.show({
        text: response.error_description,
        type: 'danger',
      });
    }
  };

  isSessionCookieSet = () => !!this.sessionToken;

  isLoggedIn = () =>
    (!!this.userBean?.access_token && !!this.userBean?.userId) ||
    this.isLoggedInState;

  getUserData = () => {
    return this.userBeanService.getUserBean();
  };

  login = async (credentials: Record<string, string>, step?: number) => {
    const data = new URLSearchParams(credentials).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${API_URL}/auth`, data, { headers })
      );

      this.authenticateSession(response);

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      this.notifyError(httpError.error, step);

      throw httpError?.error;
    }
  };

  samlLogin = async (credentials: Auth) => {
    const data = new URLSearchParams(Object.entries(credentials)).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${API_URL}/user/samllogin`, data, {
          headers,
        })
      );

      this.authenticateSession(response);

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      this.notifyError(httpError.error);

      throw httpError?.error;
    }
  };

  smsLogin = async (challenge: string) => {
    const data = new URLSearchParams({ challenge }).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<Record<string, unknown>>(
          `${API_URL}/user/smslogin`,
          data,
          { headers }
        )
      );

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      throw httpError?.error;
    }
  };

  cecLogin = (cec: Record<string, unknown>) =>
    firstValueFrom(
      this.http.post<Record<string, unknown>>(
        `${API_URL}/community/ceclogin`,
        cec
      )
    );

  mobileIdLogin = () =>
    this.http
      .post<Record<string, unknown>>(`${API_URL}/user/mobileidlogin`, {})
      .pipe(
        timeout(180000),
        catchError((error: HttpErrorResponse) =>
          throwError(() => error?.error ?? {})
        )
      );

  certificateLogin = async (url: string) => {
    try {
      const response = await firstValueFrom(
        this.http.post<Record<string, unknown>>(url, {})
      );

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      this.notifyError(httpError.error);

      throw httpError?.error;
    }
  };

  otpLogin = async (challenge: string) => {
    const data = new URLSearchParams({ challenge }).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<Record<string, unknown>>(
          `${API_URL}/user/otplogin`,
          data,
          { headers }
        )
      );

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      throw httpError?.error;
    }
  };

  otpRenewChallengeLogin = async (challenge: string) => {
    const data = new URLSearchParams({ challenge }).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<Record<string, unknown>>(
          `${API_URL}/user/otprenewchallenge`,
          data,
          {
            headers,
          }
        )
      );

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      throw httpError?.error;
    }
  };

  logout = async (saveRoute: boolean = false) => {
    this.userSessionService.stopTimer();
    this.modalService.closeAll();

    if (saveRoute) {
      this.routesService.saveCurrentUrlForSessionRestore();
    }

    this.store.dispatch(setLastSearch({ payload: null }));

    const samlUser =
      this.userBean?.samlUser !== undefined ||
      this.userBean?.authenticationMethod === AuthenticationMethod.FINMA_SSO;

    if (samlUser) {
      const hasSamlAccounts =
        this.userBean?.userId &&
        this.userBean?.samlUser?.accounts !== undefined;

      if (hasSamlAccounts) {
        await this.endServerSession(`${API_URL}/user/samllogout`);

        this.samlLocalLogout();

        return;
      }

      try {
        await firstValueFrom(
          this.http.get<UserSettings>(`${API_URL}/user/settings`)
        );

        const logoutUrl = `${this.location.prepareExternalUrl('/')}agora-api/saml/logout`;

        this.resetToken();
        window.location.href = logoutUrl;
      } catch {
        this.routesService.saveCurrentUrlForSessionRestore();

        this.store.dispatch(resetUser());
        this.store.dispatch(setIsLoggedIn({ payload: false }));

        this.settingsService.resetRootData();
        this.router.navigate(['/login']);
      }

      return;
    }

    if (await this.endServerSession(`${API_URL}/logout`)) {
      this.resetToken();
    }

    this.store.dispatch(resetUser());
    this.store.dispatch(setIsLoggedIn({ payload: false }));

    this.settingsService.resetRootData();
    this.router.navigate(['/login']);
  };

  endServerSession = async (url: string) => {
    const ended = await firstValueFrom(
      this.http.get<void>(url).pipe(
        retry({
          count: 1,
          delay: (error: HttpErrorResponse) =>
            error.status === HttpStatusCode.UNAUTHORIZED
              ? throwError(() => error)
              : of(null),
        }),
        map(() => true),
        catchError((error: HttpErrorResponse) =>
          of(error.status === HttpStatusCode.UNAUTHORIZED)
        )
      )
    );

    if (!ended) {
      this.toastService.show({
        text: this.translate.instant('WEB_NOTIFY_LOGOUT_NOT_CONFIRMED'),
        type: 'danger',
        delay: 15000,
      });
    }

    return ended;
  };

  samlLocalLogout = () => {
    this.routesService.saveCurrentUrlForSessionRestore();
    this.store.dispatch(setIsLoggedIn({ payload: false }));

    this.settingsService.resetRootData();
    this.router.navigate(['/finma-communities']);
  };

  register = (registration: Record<string, unknown>) =>
    firstValueFrom(
      this.http.post<Record<string, unknown>>(
        `${API_URL}/communities/new`,
        registration
      )
    );

  samlRegister = (registration: Record<string, unknown>) =>
    firstValueFrom(
      this.http.post<Record<string, unknown>>(
        `${API_URL}/communities/samlnew`,
        registration
      )
    );

  checkCoupon = async (coupon?: Nullable<string>) => {
    if (!coupon) {
      logError('No coupon provided');

      return;
    }

    const data = new URLSearchParams({ coupon }).toString();

    try {
      const response = await firstValueFrom(
        this.http.post<Record<string, unknown>>(
          `${API_URL}/system/coupon`,
          data,
          { headers }
        )
      );

      return response;
    } catch (error: unknown) {
      const httpError = error as HttpErrorResponse;

      throw httpError?.error;
    }
  };

  getActivation = (cid: string, token: string) =>
    firstValueFrom(
      this.http.get<Record<string, unknown>>(
        `${API_URL}/community/activation?cid=${cid}&token=${token}`,
        { headers }
      )
    );

  retrieveSystemSettings = async (): Promise<SystemSettings> => {
    try {
      const response = await firstValueFrom(
        this.http.get<SystemSettings>(`${API_URL}/system/settings`, {
          params: { cache: true },
        })
      );

      return response;
    } catch {
      return {};
    }
  };

  getSystemSettings = async () => {
    if (isEmpty(this.systemSettings)) {
      const response = await this.retrieveSystemSettings();

      this.store.dispatch(setSystemSettings({ payload: response }));

      return response;
    }

    return this.systemSettings;
  };
}
